import { apiUrl } from "../utils/api";
import { invalidateByPrefix } from "../utils/swrCache";
import type { NoteDto } from "./vorgangService";

export interface MentionItemDto {
    noteId: number;
    todoId: number | null;
    todoTitel: string | null;
    author: string;
    text: string;
    timestamp: string;
    read: boolean;
}

async function req<T>(url: string, options?: RequestInit): Promise<T> {
    const res = await fetch(url, {
        headers: { "Content-Type": "application/json" },
        ...options,
    });
    if (!res.ok) {
        const txt = await res.text();
        throw new Error(`API error ${res.status}: ${txt}`);
    }
    if (res.status === 204) return undefined as T;
    return res.json();
}

export const noteService = {
    async create(
        todoId: number,
        input: {
            text: string;
            mentions?: string[];
            parentId?: number | null;
            riskObjectId?: number | null;
            contractId?: number | null;
            /** `'html'` = Blockeditor-Inhalt (PO 20.09.2026). */
            format?: "text" | "html";
            /** Die markierte Textstelle, an der der Kommentar hängt (PO 18.09.2026). */
            anker?: string;
        },
    ): Promise<NoteDto> {
        const created = await req<NoteDto>(apiUrl(`todo/${todoId}/notes`), {
            method: "POST",
            body: JSON.stringify(input),
        });
        invalidateByPrefix("todo.");
        return created;
    },

    async update(
        noteId: number,
        input: {
            text: string;
            mentions?: string[];
            riskObjectId?: number | null;
            contractId?: number | null;
            /**
             * `'html'` = Blockeditor-Inhalt (PO 20.09.2026). Ohne Angabe
             * bleibt das Format, wie es ist — eine geänderte Erwähnung darf
             * Klartext nicht zu Markup erklären.
             */
            format?: "text" | "html";
        },
    ): Promise<NoteDto> {
        const updated = await req<NoteDto>(apiUrl(`todo-notes/${noteId}`), {
            method: "PUT",
            body: JSON.stringify(input),
        });
        invalidateByPrefix("todo.");
        return updated;
    },

    async remove(noteId: number): Promise<void> {
        await req<void>(apiUrl(`todo-notes/${noteId}`), { method: "DELETE" });
        invalidateByPrefix("todo.");
    },
    /**
     * DEN HAKEN AM KOMMENTAR SETZEN ODER ZURÜCKNEHMEN (PO 18.09.2026) —
     * Zwilling von `vorgangNoteService.setErledigt`.
     *
     * Es braucht ihn hier, weil ein Kommentar an einer To-do-Notiz selbst
     * wieder eine To-do-Notiz ist (beide Tabellen haben eigene Id-Folgen).
     */
    async setErledigt(noteId: number, erledigt: boolean): Promise<NoteDto> {
        const updated = await req<NoteDto>(apiUrl(`todo-notes/${noteId}/erledigt`), {
            method: "PUT",
            body: JSON.stringify({ erledigt }),
        });
        invalidateByPrefix("todo.");
        return updated;
    },

    /** T4-PR3: Notiz `sourceId` in `targetId` zusammenführen (Quelle wird archiviert). */
    async merge(sourceId: number, targetId: number): Promise<void> {
        await req<{ ok: true }>(apiUrl(`todo-notes/${sourceId}/merge`), {
            method: "POST",
            body: JSON.stringify({ targetId }),
        });
        invalidateByPrefix("todo.");
    },

    async listForTodo(todoId: number): Promise<NoteDto[]> {
        return req<NoteDto[]>(apiUrl(`todo/${todoId}/notes`));
    },

    async myMentions(): Promise<MentionItemDto[]> {
        return req<MentionItemDto[]>(apiUrl("todo-notes/mentions/me"));
    },

    async markMentionRead(noteId: number): Promise<void> {
        await req<void>(apiUrl(`todo-notes/mentions/${noteId}/read`), { method: "PATCH" });
    },
};
